import React from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Card,
  Select,
  DatePicker,
  Upload,
  Popover,
  Switch,
  Tag,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import UploadUtils from "../../../utilities/uploadUtils";
import dayjs from "dayjs";

const { TextArea } = Input;

const modules = {
  toolbar: {
    container: [
      [{ header: [2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  },
};

const BlogDetail = ({ data = {}, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div style={{ padding: "8px 0" }}>
      <Row gutter={16}>
        {/* Left Column: Article Body & Excerpt */}
        <Col span={16}>
          <Card
            size="small"
            title="Article Content & Headline"
            type="inner"
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              label="Blog Title"
              required
              rules={[{ required: true, message: "Please add blog title!" }]}
            >
              <Input
                value={data.title || ""}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="e.g. Chrysler Uconnect Touch Screen Repair Guide"
              />
            </Form.Item>

            <Form.Item
              label="Article Subtitle / Heading"
              required
              rules={[{ required: true, message: "Please add article heading!" }]}
            >
              <Input
                value={data.heading || ""}
                onChange={(e) => handleChange("heading", e.target.value)}
                placeholder="e.g. How to fix reboot loops and un-responsive digitizers in 2024"
              />
            </Form.Item>

            <Form.Item
              label="Full Article Description"
              required
              rules={[{ required: true, message: "Please add article description!" }]}
            >
              <ReactQuill
                theme="snow"
                rows={6}
                modules={modules}
                value={data.content || ""}
                onChange={(val) => handleChange("content", val)}
              />
            </Form.Item>
          </Card>

          <Card size="small" title="Blog Summary & Excerpt" type="inner">
            <Form.Item
              label="Short Summary Excerpt"
              required
              rules={[{ required: true, message: "Please add excerpt summary!" }]}
            >
              <ReactQuill
                theme="snow"
                rows={3}
                value={data.excerpt || ""}
                onChange={(val) => handleChange("excerpt", val)}
                placeholder="Brief 2-3 sentence overview for listing cards and search previews..."
              />
            </Form.Item>
          </Card>
        </Col>

        {/* Right Column: Featured Image & Article Metadata */}
        <Col span={8}>
          <Card size="small" title="Featured Image" type="inner" style={{ marginBottom: 16 }}>
            <Form.Item
              label={
                <span>
                  Cover Image{" "}
                  <Popover placement="right" title="Recommended W * H" content="1200 x 675">
                    <QuestionCircleOutlined />
                  </Popover>
                </span>
              }
              required
              rules={[{ required: true, message: "Please upload featured image!" }]}
              {...UploadUtils.formItemProps}
            >
              <Upload
                {...UploadUtils.buttonPreviewProps}
                fileList={
                  data.feature_image
                    ? [{ uid: "-1", name: "cover.png", status: "done", url: data.feature_image }]
                    : []
                }
                onChange={(info) => {
                  const processed = UploadUtils.processUploadImage(info);
                  handleChange("feature_image", processed);
                }}
              >
                <UploadUtils.CardContent />
              </Upload>
            </Form.Item>
          </Card>

          <Card size="small" title="Organization & Meta" type="inner">
            <Form.Item
              label="Author Name"
              required
              rules={[{ required: true, message: "Please specify author name!" }]}
            >
              <Input
                value={data.author || ""}
                onChange={(e) => handleChange("author", e.target.value)}
                placeholder="e.g. Master Tech Alex"
              />
            </Form.Item>

            <Form.Item
              label="Blog Category"
              required
              rules={[{ required: true, message: "Please select blog category!" }]}
            >
              <Select
                value={data.category || "Repair Guides"}
                onChange={(val) => handleChange("category", val)}
                options={[
                  { label: "Repair Guides & DIY", value: "Repair Guides" },
                  { label: "GPS & Navigation Fixes", value: "Navigation Fixes" },
                  { label: "Car Radio Decoding", value: "Radio Decoding" },
                  { label: "Hardware & Screen Fixes", value: "Hardware Repair" },
                  { label: "Firmware Updates", value: "Firmware Updates" },
                ]}
              />
            </Form.Item>

            <Form.Item label="Blog Tags">
              <Select
                mode="tags"
                style={{ width: "100%" }}
                value={data.tags || []}
                onChange={(val) => handleChange("tags", val)}
                tokenSeparators={[","]}
                placeholder="e.g. uconnect, screen, touch, jeep"
              />
            </Form.Item>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Reading Time">
                  <Input
                    value={data.reading_time || "5 min read"}
                    onChange={(e) => handleChange("reading_time", e.target.value)}
                    placeholder="e.g. 5 min read"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                {data.__pageStatus === "ACTIVE" ? (
                  <Form.Item label="Publish Status">
                    <div style={{ padding: "4px 0" }}>
                      <Tag color="green" style={{ fontSize: 14, padding: "4px 12px" }}>
                        Published
                      </Tag>
                    </div>
                  </Form.Item>
                ) : (
                  <Form.Item label="Publish Date & Time">
                    <DatePicker
                      showTime
                      style={{ width: "100%" }}
                      value={
                        data.visibility_date && dayjs(data.visibility_date).isValid()
                          ? dayjs(data.visibility_date)
                          : null
                      }
                      onChange={(date) =>
                        handleChange(
                          "visibility_date",
                          date && date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss") : null,
                        )
                      }
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>

            <Row gutter={8} style={{ marginTop: 8 }}>
              <Col span={14}>
                <Form.Item label="Article Visibility" style={{ marginBottom: 0 }}>
                  <Select
                    value={data.visibility || "public"}
                    onChange={(val) => handleChange("visibility", val)}
                    options={[
                      { label: "Public (Visible on Site)", value: "public" },
                      { label: "Draft (Hidden / Unlisted)", value: "draft" },
                      { label: "Wholesalers Only", value: "wholesalers" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={10} style={{ textAlign: "right" }}>
                <Form.Item label="Featured Article" style={{ marginBottom: 0 }}>
                  <Switch
                    checked={data.is_featured ?? false}
                    onChange={(checked) => handleChange("is_featured", checked)}
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BlogDetail;
